/**
 * Resolves effective page permissions from `roleAuthorization[]`.
 * Queue monitors share one permission union; asset sub-pages inherit Varlıklar permissions.
 */

/** Queue monitor UIs share the same permission union. */
export const QUEUE_MONITOR_PAGE_KEYS = Object.freeze([
    'platformQueueMonitor',
    'userOperationsQueueMonitor',
]);

/** General archive keeps legacy BIGDES role grants effective during migration. */
export const REPORT_ARCHIVE_PAGE_KEYS = Object.freeze([
    'reportArchive',
    'bigdesReports',
]);

/** Asset sub-pages inherit Varlıklar permissions when their own entry is empty. */
export const ASSET_LIFECYCLE_PAGE_KEYS = Object.freeze([
    'assetInventoryLicenseManagement',
    'assetInventoryDisposalManagement',
    'assetInventoryLifecycleEvents',
    'assetInventoryMaintenance',
]);

/**
 * Core shell pages every authenticated user can open (Home includes Dashboard).
 * Granted as View even when roleAuthorization omits them.
 */
export const ALWAYS_VIEW_PAGE_KEYS = Object.freeze(['home', 'myWork']);

const ASSET_LIFECYCLE_INHERIT_PAGE = 'assetInventoryAssets';

/**
 * @param {string} pageKey
 * @param {Iterable<string>} perms
 * @returns {string[]}
 */
function withAlwaysView(pageKey, perms) {
    if (!ALWAYS_VIEW_PAGE_KEYS.includes(pageKey)) return [...perms];
    const set = new Set(perms);
    set.add('View');
    return [...set];
}

/**
 * @param {string} pageKey
 * @returns {string[]}
 */
export function expandRoleAuthorizationLookupKeys(pageKey) {
    const keys = new Set([pageKey]);

    if (QUEUE_MONITOR_PAGE_KEYS.includes(pageKey)) {
        for (const k of QUEUE_MONITOR_PAGE_KEYS) keys.add(k);
    }
    if (REPORT_ARCHIVE_PAGE_KEYS.includes(pageKey)) {
        for (const k of REPORT_ARCHIVE_PAGE_KEYS) keys.add(k);
    }

    return [...keys];
}

/**
 * @param {Array<{ roleAuthorization?: Array<{ page: string, permissions: string[] }> }>} roles
 * @param {string} pageKey
 * @returns {string[]}
 */
export function mergePermissionsForPageFromRoles(roles, pageKey) {
    const set = new Set();
    const lookupKeys = expandRoleAuthorizationLookupKeys(pageKey);

    for (const role of roles || []) {
        const auth = role?.roleAuthorization || [];
        for (const pk of lookupKeys) {
            const pagePermission = auth.find((a) => a.page === pk);
            for (const p of pagePermission?.permissions || []) {
                set.add(p);
            }
        }
        if (ASSET_LIFECYCLE_PAGE_KEYS.includes(pageKey)) {
            const assetsRow = auth.find((a) => a.page === ASSET_LIFECYCLE_INHERIT_PAGE);
            for (const p of assetsRow?.permissions || []) {
                set.add(p);
            }
        }
    }

    return withAlwaysView(pageKey, set);
}

/**
 * Same rules as {@link mergePermissionsForPageFromRoles} on a pre-built page→permissions map.
 * @param {Record<string, string[]>} permissionMap
 * @param {string} pageKey
 * @returns {string[]}
 */
export function getEffectivePermissionsFromMap(permissionMap, pageKey) {
    let perms = permissionMap[pageKey] ? [...permissionMap[pageKey]] : [];

    for (const pk of expandRoleAuthorizationLookupKeys(pageKey)) {
        if (pk === pageKey) continue;
        const alt = permissionMap[pk];
        if (alt?.length) {
            perms = [...new Set([...perms, ...alt])];
        }
    }

    if (ASSET_LIFECYCLE_PAGE_KEYS.includes(pageKey)) {
        const assetPerms = permissionMap[ASSET_LIFECYCLE_INHERIT_PAGE];
        if (assetPerms?.length) {
            perms = [...new Set([...perms, ...assetPerms])];
        }
    }

    return withAlwaysView(pageKey, perms);
}
